import { AfterViewInit, Component, Input, OnInit, Renderer2 } from '@angular/core';
import { PropertyStoreService } from './services/stores/property-store.service';
import { UpdateServiceWorkerService } from './services/update-service-worker.service';
import { RowButton } from './panel-components/models/row-button';
import { MenuStoreService } from './services/stores/menu-store.service';
import { LoadingOverlayStoreService } from './services/stores/loading-overlay-store.service';
import { Property } from './domain/property';
import { productFruits } from 'product-fruits';
import { environment } from '../environments/environment';
import { AuthService } from '@auth0/auth0-angular';
import { UserService } from './services/user.service';
import { UserQueryRequest } from './services/request/user-query-request';
import { PermissionsStoreService } from './services/stores/permission-store-service';
import { firstValueFrom } from 'rxjs';

declare global {
   interface Window {
      productFruits: any;
   }
}

@Component({
   selector: 'app-root',
   templateUrl: './app.component.html',
   styleUrls: ['./app.component.scss']
})
export class AppComponent implements AfterViewInit, OnInit {
   @Input() AuthService: any;

   topbarMenuActive: boolean | undefined;
   overlayMenuActive: boolean | undefined;
   staticMenuMobileActive: boolean | undefined;
   menuClick: boolean | undefined;
   topbarItemClick: boolean | undefined;
   activeTopbarItem: any;
   documentClickListener: (() => void) | undefined;
   configActive: boolean | undefined;
   configClick: boolean | undefined;
   rightPanelActive: boolean | undefined;
   rightPanelClick: boolean | undefined;
   isMenuActive = true;
   menuHoverActive = false;
   searchClick = false;
   search = false;
   isPropertyDropdownReadOnly: boolean;
   headingButtons: RowButton[];
   isLoading: boolean;
   showTermsAndConditions: boolean;
   currentProperty: Property | undefined;
   isOrgWizardVisible = false;

   constructor(
      public renderer: Renderer2,
      private propStore: PropertyStoreService,
      private permissionsStore: PermissionsStoreService,
      private sw: UpdateServiceWorkerService,
      private menuStore: MenuStoreService,
      private loadingOverlayStore: LoadingOverlayStoreService,
      private auth: AuthService,
      private userService: UserService
   ) {
      this.showTermsAndConditions = false;
      this.menuClick = false;
      this.isPropertyDropdownReadOnly = false;
      this.isLoading = true;

      this.loadingOverlayStore.LoadingState.subscribe((state) => {
         this.isLoading = state.Properties;
      });

      if (this.isMobile() || this.isTablet()) {
         this.isMenuActive = false;
      }
      this.sw.checkForUpdates();
      this.headingButtons = [
         new RowButton(
            'back',
            '',
            'fal fa-arrow-left fa-1x',
            ['p-button-rounded'],
            ['p-col-1', 'p-text-left', 'p-pl-1']
         )
      ];

      this.menuStore.menuState.subscribe((value) => {
         this.isMenuActive = value;
      });
   }

   ngOnInit(): void {
      this.propStore.currentProperty.subscribe(async (currentProp) => {
         // Always keep local reference to avoid race conditions
         this.currentProperty = currentProp;

         // Default to hidden unless conditions below pass
         this.isOrgWizardVisible = false;

         if (!currentProp?.isPhobosEnabled) {
            return;
         }

         // Use cached permissions store (single shared fetch for whole app)
         const permissions = await firstValueFrom(this.permissionsStore.get());
         if (
            permissions.taskManagement.create &&
            currentProp.propertyPmsResponse?.pmsId &&
            currentProp.organisations.length > 0
         ) {
            this.isOrgWizardVisible = !currentProp.hasCompletedOnBoarding;
         }
      });

      this.loadPf();
   }

   // Detect browser language.
   loadPf = () => {
      let initLanguage: any = null;

      switch (navigator.language) {
         case 'fr':
            initLanguage = 'fr';
            break;
         case 'fr-FR':
            initLanguage = 'fr';
            break;
         case 'fr-fr':
            initLanguage = 'fr';
            break;
         case 'ja':
            initLanguage = 'ja';
            break;
         case 'ja-JA':
            initLanguage = 'ja';
            break;
         case 'ja-ja':
            initLanguage = 'ja';
            break;
         default:
            initLanguage = 'en';
      }

      this.auth.user$.subscribe((user: any) => {
         if (user) {
            const userQuery = new UserQueryRequest();
            userQuery.currentUser = true;
            this.userService.queryUser(userQuery).subscribe((appUser) => {
               const first = appUser.user.firstName;
               const last = appUser.user.lastName;

               const welcome_name = appUser.user.firstName
                  ? `${first} ${last}`
                  : user?.email;
               productFruits.init(
                  environment.productFruit.workspaceCode,
                  initLanguage,
                  {
                     username: user?.email,
                     firstname: welcome_name,
                     role: user?.metaData?.has_been_through_signup,
                     signUpAt: user?.created_date
                  },
                  { disableLocationChangeDetection: false }
               );
            });
         }
      });
   };

   ngAfterViewInit() {
      this.documentClickListener = this.renderer.listen('body', 'click', () => {
         if (!this.topbarItemClick) {
            this.activeTopbarItem = null;
            this.topbarMenuActive = false;
         }

         if (this.configActive && !this.configClick) {
            this.configActive = false;
         }

         if (!this.rightPanelClick) {
            this.rightPanelActive = false;
         }

         if (!this.menuClick) {
            if (this.overlayMenuActive) {
               this.overlayMenuActive = false;
            }
            if (this.staticMenuMobileActive) {
               this.staticMenuMobileActive = false;
            }

            this.menuHoverActive = false;
            this.unblockBodyScroll();
         }

         if (!this.searchClick) {
            this.search = false;
         }

         this.searchClick = false;
         this.configClick = false;
         this.topbarItemClick = false;
         this.menuClick = false;
         this.rightPanelClick = false;
      });
      if (this.isMobile() || this.isTablet()) {
         this.isMenuActive = false;
      }
   }

   isMobileActive() {
      if (this.isMobile() || this.isTablet()) {
         return this.isMenuActive;
      }
      return false;
   }

   onMenuToggle() {
      this.menuStore.setMenuState(!this.isMenuActive, true);
   }

   isStatic() {
      return this.isDesktop();
   }

   isTablet() {
      const width = window.innerWidth;
      return width <= 1024 && width > 640;
   }

   isDesktop() {
      return window.innerWidth > 1024;
   }

   isMobile() {
      return window.innerWidth <= 640;
   }

   unblockBodyScroll(): void {
      if (document.body.classList) {
         document.body.classList.remove('blocked-scroll');
      } else {
         document.body.className = document.body.className.replace(
            new RegExp(
               '(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)',
               'gi'
            ),
            ' '
         );
      }
   }

   onOrgWizardClose() {
      this.isOrgWizardVisible = false;
   }
}
